"use client";
import { useRef, useState } from "react";
import * as XLSX from "xlsx";
import axios from "axios";
import { ModalCustom } from "../../../components/custom/modal-custom";
import { showToast } from "../../../components/custom/custom-toast";
import { ApiConfig, API_EQUIPMENT_ROUTER } from "../../../services/api-config";
import CookieUtils from "../../../utils/cookie-utils";
import { EQUIPMENT_TYPE, EQUIPMENT_TYPE_NAME } from "../services/equipment-service";

interface ImportResult {
  success: number;
  failed: number;
  errors: Array<{ row: number; message: string }>;
}

interface ParsedRow {
  code: string;
  name: string;
  type: number | null;
  typeText: string;
  capacity: number | null;
  note: string;
  _error?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  branchId: number | undefined;
  onSuccess?: () => void;
}

// Map text/enum của cột type (linh hoạt): "1" hoặc "Túi" hoặc "bag" đều hợp lệ
const TYPE_ALIAS: Record<string, number> = {
  "1": EQUIPMENT_TYPE.BAG,
  "tui": EQUIPMENT_TYPE.BAG,
  "túi": EQUIPMENT_TYPE.BAG,
  "bag": EQUIPMENT_TYPE.BAG,
  "2": EQUIPMENT_TYPE.WASHING_MACHINE,
  "may giat": EQUIPMENT_TYPE.WASHING_MACHINE,
  "máy giặt": EQUIPMENT_TYPE.WASHING_MACHINE,
  "washing": EQUIPMENT_TYPE.WASHING_MACHINE,
  "washing_machine": EQUIPMENT_TYPE.WASHING_MACHINE,
  "3": EQUIPMENT_TYPE.DRYER,
  "may say": EQUIPMENT_TYPE.DRYER,
  "máy sấy": EQUIPMENT_TYPE.DRYER,
  "dryer": EQUIPMENT_TYPE.DRYER,
  "4": EQUIPMENT_TYPE.IRON,
  "ban ui": EQUIPMENT_TYPE.IRON,
  "bàn ủi": EQUIPMENT_TYPE.IRON,
  "iron": EQUIPMENT_TYPE.IRON,
  "5": EQUIPMENT_TYPE.FOLDING,
  "ban gap": EQUIPMENT_TYPE.FOLDING,
  "bàn gấp": EQUIPMENT_TYPE.FOLDING,
  "folding": EQUIPMENT_TYPE.FOLDING,
  "6": EQUIPMENT_TYPE.BASKET,
  "sot": EQUIPMENT_TYPE.BASKET,
  "sọt": EQUIPMENT_TYPE.BASKET,
  "basket": EQUIPMENT_TYPE.BASKET,
};

function parseType(raw: any): number | null {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim().toLowerCase();
  return TYPE_ALIAS[s] ?? (!isNaN(Number(s)) ? Number(s) : null);
}

const EquipmentImportModal: React.FC<Props> = ({ isOpen, onClose, branchId, onSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<ParsedRow[]>([]);
  const [skipErrors, setSkipErrors] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const token = CookieUtils.getCurrentUser().access_token ?? "";
  const headers = {
    Authorization: `Bearer ${token}`,
    "x-svc-id": ApiConfig.PROJECT_ID.ORDER_SERVICE,
    platform: "1",
  };

  const resetAll = () => {
    setFile(null);
    setParsed([]);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    resetAll();
    onClose();
  };

  const handleDownloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ["Mã thiết bị (*)", "Tên thiết bị", "Loại (*)", "Sức chứa (kg)", "Ghi chú"],
      ["MG001", "Máy giặt 10kg", "Máy giặt", 10, "Máy giặt công nghiệp"],
      ["MS001", "Máy sấy 8kg", "Máy sấy", 8, ""],
      ["ST001", "Sọt số 1", "Sọt", "", ""],
    ]);
    ws["!cols"] = [{ wch: 18 }, { wch: 30 }, { wch: 14 }, { wch: 14 }, { wch: 30 }];

    // Sheet hướng dẫn
    const wsGuide = XLSX.utils.aoa_to_sheet([
      ["HƯỚNG DẪN IMPORT THIẾT BỊ"],
      [""],
      ["1. Cột (*) là bắt buộc"],
      ["2. Mã thiết bị: duy nhất trong 1 chi nhánh. Không trùng."],
      ["3. Loại: có thể nhập tên hoặc số theo danh sách dưới:"],
      ["   - Túi (1)"],
      ["   - Máy giặt (2)"],
      ["   - Máy sấy (3)"],
      ["   - Bàn ủi (4)"],
      ["   - Bàn gấp (5)"],
      ["   - Sọt (6)"],
      ["4. Sức chứa: để trống nếu không áp dụng."],
      ["5. Mỗi thiết bị tạo ra sẽ có status = Khả dụng."],
    ]);
    wsGuide["!cols"] = [{ wch: 80 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Thiết bị");
    XLSX.utils.book_append_sheet(wb, wsGuide, "Hướng dẫn");
    XLSX.writeFile(wb, "mau-import-thiet-bi.xlsx");
  };

  const handleFileChange = (f: File | null) => {
    setFile(f);
    setParsed([]);
    setResult(null);
    if (!f) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = new Uint8Array(ev.target?.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json: any[] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });

        // Skip header row
        const rows: ParsedRow[] = [];
        for (let i = 1; i < json.length; i++) {
          const r = json[i];
          if (!r || r.length === 0) continue;
          const code = String(r[0] ?? "").trim();
          if (!code) continue; // skip empty row
          const typeText = String(r[2] ?? "").trim();
          const type = parseType(r[2]);
          rows.push({
            code,
            name: String(r[1] ?? "").trim(),
            type,
            typeText,
            capacity: r[3] && !isNaN(Number(r[3])) ? Number(r[3]) : null,
            note: String(r[4] ?? "").trim(),
            _error: !type ? "Loại thiết bị không hợp lệ" : undefined,
          });
        }
        setParsed(rows);
      } catch (err: any) {
        showToast({ message: "File không đúng định dạng Excel", type: "error" });
        console.error(err);
      }
    };
    reader.readAsArrayBuffer(f);
  };

  const validCount = parsed.filter((r) => !r._error).length;

  const handleImport = async () => {
    if (!branchId) {
      showToast({ message: "Chưa chọn chi nhánh", type: "error" });
      return;
    }
    if (parsed.length === 0) {
      showToast({ message: "Chưa có dữ liệu để import", type: "error" });
      return;
    }

    const items = parsed
      .filter((r) => skipErrors ? !r._error : true)
      .map((r) => ({
        code: r.code,
        name: r.name,
        type: r.type as number,
        capacity: r.capacity ?? undefined,
        note: r.note || undefined,
      }));

    if (items.length === 0) {
      showToast({ message: "Không có dòng hợp lệ để import", type: "warning" });
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${ApiConfig.API_URL}/api/${API_EQUIPMENT_ROUTER.API_BULK_CREATE}`,
        { branch_id: branchId, items, skip_errors: skipErrors },
        { headers, validateStatus: () => true },
      );
      if (res.status >= 400 || res.data?.status >= 400) {
        const msg = res.data?.message || res.data?.data?.message || `Lỗi ${res.status}`;
        showToast({ message: msg, type: "error" });
        return;
      }
      const data: ImportResult = res.data?.data || res.data;
      if (data && typeof data.success === "number") {
        setResult(data);
        if (data.success > 0) {
          showToast({ message: `Import thành công ${data.success} thiết bị`, type: "success" });
          onSuccess?.();
        }
        if (data.failed > 0 && data.success === 0) {
          showToast({ message: `Import thất bại: ${data.failed} dòng lỗi`, type: "error" });
        }
      }
    } catch (err: any) {
      showToast({ message: err?.message || "Lỗi import", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const body = (
    <>
      <div className="space-y-4">
        {/* Bước 1: Tải mẫu */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-sm text-blue-800 font-medium mb-2">
            Bước 1: Tải file mẫu Excel
          </p>
          <button
            type="button"
            onClick={handleDownloadTemplate}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            Tải file mẫu (.xlsx)
          </button>
        </div>

        {/* Bước 2: Chọn file */}
        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl p-4">
          <p className="text-sm text-gray-700 font-medium mb-2">
            Bước 2: Chọn file Excel đã điền
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
          />
          {file && (
            <p className="mt-2 text-sm text-green-600 font-medium">
              Đã chọn: {file.name} · {parsed.length} dòng · Hợp lệ: {validCount}
            </p>
          )}
        </div>

        {/* Preview bảng */}
        {parsed.length > 0 && (
          <div className="border rounded-xl overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700">
              Preview ({parsed.length} dòng)
            </div>
            <div className="max-h-64 overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 text-left text-gray-700">
                  <tr>
                    <th className="px-3 py-2 w-12">#</th>
                    <th className="px-3 py-2">Mã</th>
                    <th className="px-3 py-2">Tên</th>
                    <th className="px-3 py-2">Loại</th>
                    <th className="px-3 py-2">Sức chứa</th>
                    <th className="px-3 py-2">Ghi chú</th>
                    <th className="px-3 py-2">Tình trạng</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.slice(0, 50).map((r, i) => (
                    <tr key={i} className={r._error ? "bg-red-50" : ""}>
                      <td className="px-3 py-1.5 text-gray-500">{i + 2}</td>
                      <td className="px-3 py-1.5 font-semibold">{r.code}</td>
                      <td className="px-3 py-1.5">{r.name || "—"}</td>
                      <td className="px-3 py-1.5">
                        {r.type != null ? EQUIPMENT_TYPE_NAME[r.type] : (
                          <span className="text-red-600">Không hợp lệ: {r.typeText}</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5">{r.capacity ?? "—"}</td>
                      <td className="px-3 py-1.5 text-gray-500">{r.note || "—"}</td>
                      <td className="px-3 py-1.5">
                        {r._error ? (
                          <span className="inline-flex px-2 py-0.5 text-xs rounded bg-red-100 text-red-700">
                            {r._error}
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-0.5 text-xs rounded bg-green-100 text-green-700">
                            Hợp lệ
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {parsed.length > 50 && (
                    <tr>
                      <td colSpan={7} className="px-3 py-2 text-xs text-center text-gray-500">
                        ... và {parsed.length - 50} dòng khác
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Options */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={skipErrors}
            onChange={(e) => setSkipErrors(e.target.checked)}
            className="w-4 h-4"
          />
          <span className="text-sm text-gray-700">Bỏ qua dòng lỗi, chỉ import dòng hợp lệ</span>
        </label>

        {/* Kết quả */}
        {result && (
          <div className="border rounded-xl overflow-hidden">
            <div className="flex gap-4 p-4 bg-gray-50">
              <div className="flex items-center gap-2">
                <span className="inline-block w-3 h-3 rounded-full bg-green-500" />
                <span className="text-sm font-bold text-green-700">Thành công: {result.success}</span>
              </div>
              {result.failed > 0 && (
                <div className="flex items-center gap-2">
                  <span className="inline-block w-3 h-3 rounded-full bg-red-500" />
                  <span className="text-sm font-bold text-red-700">Thất bại: {result.failed}</span>
                </div>
              )}
            </div>
            {result.errors.length > 0 && (
              <div className="max-h-48 overflow-auto p-2 text-sm">
                {result.errors.map((er, i) => (
                  <div key={i} className="py-1 border-b last:border-0">
                    <span className="font-semibold text-red-600">Dòng {er.row}:</span>{" "}
                    <span className="text-gray-700">{er.message}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    </>
  );

  const footer = (
    <>
      <button
        type="button"
        onClick={handleClose}
        className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium"
      >
        Đóng
      </button>
      <button
        type="button"
        onClick={handleImport}
        disabled={loading || parsed.length === 0}
        className="px-5 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 text-sm font-semibold"
      >
        {loading ? "Đang import..." : `Import ${validCount > 0 ? validCount : ""} thiết bị`}
      </button>
    </>
  );

  return (
    <ModalCustom
      isOpen={isOpen}
      onClose={handleClose}
      titleModal="Import thiết bị từ Excel"
      modalSize="800px"
      bodyModal={body}
      footerModal={footer}
    />
  );
};

export default EquipmentImportModal;
